import { apiClient } from "@/lib/apiClient";
import type {
  ApprovalDecisionPayload,
  ApprovalFlow,
  ApprovalFlowListParams,
  ApprovalRequest,
  ApprovalRequestListParams,
  CreateApprovalFlowPayload,
  InsertApprovalFlowStepsPayload,
  UpdateApprovalFlowPayload,
} from "../types/approval-flows.types";

const FLOWS_ENDPOINT = "/api/admin/approval-flows";
const REQUESTS_ENDPOINT = "/api/admin/approval-requests";

/** Backend zarfı: { success, status, data, count, message }. */
interface Envelope<T> {
  success: boolean;
  status: number;
  data: T;
  items?: T;
  count?: number;
  message?: string;
}

/**
 * Liste uçları kaydı `data` altında döndürür ama bazı modüllerde `items` de
 * kullanılıyor; her ikisini de karşılayacak şekilde okuyoruz.
 */
function readList<T>(body: Envelope<T[]>): { data: T[]; total: number } {
  const list = body.items ?? body.data ?? [];
  return { data: Array.isArray(list) ? list : [], total: body.count ?? 0 };
}

export const approvalFlowsApi = {
  /** GET /api/admin/approval-flows — akış listesi (adımlar taşınmaz). */
  getAll: async (
    params?: ApprovalFlowListParams,
  ): Promise<{ data: ApprovalFlow[]; total: number }> => {
    const { data } = await apiClient.get<Envelope<ApprovalFlow[]>>(FLOWS_ENDPOINT, {
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 20,
        search: params?.search,
        targetType: params?.targetType,
        isActive: params?.isActive,
      },
    });
    return readList(data);
  },

  /** GET /api/admin/approval-flows/{id} — akış + sıralı adımlar. */
  getById: async (id: string): Promise<ApprovalFlow> => {
    const { data } = await apiClient.get<Envelope<ApprovalFlow>>(`${FLOWS_ENDPOINT}/${id}`);
    return data.data;
  },

  /** POST /api/admin/approval-flows — akış + N sıralı adım tek istekte. */
  create: async (payload: CreateApprovalFlowPayload): Promise<ApprovalFlow> => {
    const { data } = await apiClient.post<Envelope<ApprovalFlow>>(FLOWS_ENDPOINT, payload);
    return data.data;
  },

  /** PATCH /api/admin/approval-flows/{id} — steps gönderilirse adım listesi yeniden yazılır. */
  update: async (id: string, payload: UpdateApprovalFlowPayload): Promise<ApprovalFlow> => {
    const { data } = await apiClient.patch<Envelope<ApprovalFlow>>(
      `${FLOWS_ENDPOINT}/${id}`,
      payload,
    );
    return data.data;
  },

  /**
   * PATCH /api/admin/approval-flows/{id}/default — akışı "sistemin onay akışı" yapar.
   * Aynı hedef türdeki önceki işaret kalkar. Akış aktif ve adımlı olmalı.
   */
  setDefault: async (id: string): Promise<ApprovalFlow> => {
    const { data } = await apiClient.patch<Envelope<ApprovalFlow>>(
      `${FLOWS_ENDPOINT}/${id}/default`,
    );
    return data.data;
  },

  /** POST /api/admin/approval-flows/{id}/steps — seçilen adımın öncesine/sonrasına N adım. */
  insertSteps: async (
    id: string,
    payload: InsertApprovalFlowStepsPayload,
  ): Promise<ApprovalFlow> => {
    const { data } = await apiClient.post<Envelope<ApprovalFlow>>(
      `${FLOWS_ENDPOINT}/${id}/steps`,
      payload,
    );
    return data.data;
  },

  /** PATCH /api/admin/approval-flows/{id}/steps/reorder — tüm adım ID'leri istenen sırayla. */
  reorderSteps: async (id: string, stepIds: string[]): Promise<ApprovalFlow> => {
    const { data } = await apiClient.patch<Envelope<ApprovalFlow>>(
      `${FLOWS_ENDPOINT}/${id}/steps/reorder`,
      { stepIds },
    );
    return data.data;
  },

  /**
   * DELETE /api/admin/approval-flows/{id}
   *
   * Backend soft delete uygular: akış listeden ve akış çözümlemesinden düşer,
   * talepler onay geçmişi olarak yerinde kalır. Karar bekleyen talepler silmeyi
   * engellemez — adım kopyalarıyla yürüdükleri için karara açık kalırlar.
   */
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`${FLOWS_ENDPOINT}/${id}`);
  },
};

export const approvalRequestsApi = {
  /** GET /api/admin/approval-requests — awaitingMyDecision=true ile "Onay Kutum". */
  getAll: async (
    params?: ApprovalRequestListParams,
  ): Promise<{ data: ApprovalRequest[]; total: number }> => {
    const { data } = await apiClient.get<Envelope<ApprovalRequest[]>>(REQUESTS_ENDPOINT, {
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 20,
        search: params?.search,
        status: params?.status,
        flowId: params?.flowId,
        targetType: params?.targetType,
        targetId: params?.targetId,
        mine: params?.mine,
        awaitingMyDecision: params?.awaitingMyDecision,
      },
    });
    return readList(data);
  },

  /** GET /api/admin/approval-requests/{id} — adım zaman çizelgesiyle. */
  getById: async (id: string): Promise<ApprovalRequest> => {
    const { data } = await apiClient.get<Envelope<ApprovalRequest>>(`${REQUESTS_ENDPOINT}/${id}`);
    return data.data;
  },

  /*
   * Talep oluşturma ucu bilerek sarmalanmıyor: süreci eğitim talebi geldiğinde
   * backend kendisi başlatıyor (EducationEnrollmentApprovalService). Panelden
   * elle açılan talep hedef kayda bağlanmadığı için onaylansa bile hiçbir şeyi
   * değiştirmiyordu.
   */

  /** POST /api/admin/approval-requests/{id}/decision — sırası gelen adıma karar. */
  decide: async (id: string, payload: ApprovalDecisionPayload): Promise<ApprovalRequest> => {
    const { data } = await apiClient.post<Envelope<ApprovalRequest>>(
      `${REQUESTS_ENDPOINT}/${id}/decision`,
      payload,
    );
    return data.data;
  },

  /** POST /api/admin/approval-requests/{id}/cancel — devam eden talebi iptal eder. */
  cancel: async (id: string): Promise<ApprovalRequest> => {
    const { data } = await apiClient.post<Envelope<ApprovalRequest>>(
      `${REQUESTS_ENDPOINT}/${id}/cancel`,
      {},
    );
    return data.data;
  },
};
